if (!customElements.get('cart-drawer-product-spot-o2')) {
  customElements.define(
    'cart-drawer-product-spot-o2',
    class CartDrawerProductSpotO2 extends HTMLElement {
      constructor() {
        super();
        this.feedbackElement = this.querySelector('[data-cart-drawer-product-spot-feedback]');
        this.gridElement = this.querySelector('.cart-drawer-product-spot-o2__grid');
        this.onClick = this.handleClick.bind(this);
        this.onResize = this.scheduleUpdateVisibleCards.bind(this);
        this.onLoad = this.scheduleUpdateVisibleCards.bind(this);
        this.onCartUpdate = this.handleCartUpdate.bind(this);
        this.onDrawerOpened = this.handleDrawerOpened.bind(this);
        this.onSpotAnimationEnd = this.handleSpotAnimationEnd.bind(this);
        this.resizeObserver = null;
        this.resizeRafId = null;
        this.cartUpdateUnsubscriber = null;
        this.isRefreshingFromCart = false;
        this.cardAnimationTimers = new Map();
        this.motionDurationMs = null;
      }

      connectedCallback() {
        this.addEventListener('click', this.onClick);
        this.addEventListener('animationend', this.onSpotAnimationEnd);
        window.addEventListener('resize', this.onResize);
        this.addEventListener('load', this.onLoad, true);
        document.addEventListener('cart:item-added', this.onCartUpdate);
        document.addEventListener('o2:cart-updated', this.onCartUpdate);
        const hasPubSubEvents = typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS?.cartUpdate;
        if (typeof subscribe === 'function' && hasPubSubEvents) {
          // Keep product-spot sync tied to Dawn's cart pub/sub to avoid duplicate
          // refreshes after custom add-to-cart handlers that already re-render the drawer.
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
        }
        const drawerRoot = this.closest('cart-drawer');
        if (drawerRoot) {
          drawerRoot.addEventListener('transitionend', this.onDrawerOpened);
        }
        if ('ResizeObserver' in window && this.gridElement) {
          this.resizeObserver = new ResizeObserver(() => this.scheduleUpdateVisibleCards());
          this.resizeObserver.observe(this);
          this.resizeObserver.observe(this.gridElement);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('animationend', this.onSpotAnimationEnd);
        window.removeEventListener('resize', this.onResize);
        this.removeEventListener('load', this.onLoad, true);
        document.removeEventListener('cart:item-added', this.onCartUpdate);
        document.removeEventListener('o2:cart-updated', this.onCartUpdate);
        const drawerRoot = this.closest('cart-drawer');
        if (drawerRoot) {
          drawerRoot.removeEventListener('transitionend', this.onDrawerOpened);
        }
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
          this.cartUpdateUnsubscriber = null;
        }
        if (this.resizeRafId) {
          cancelAnimationFrame(this.resizeRafId);
          this.resizeRafId = null;
        }
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        }
        this.cardAnimationTimers.forEach((timerId) => clearTimeout(timerId));
        this.cardAnimationTimers.clear();
      }

      async handleClick(event) {
        if (!(event.target instanceof Element)) return;

        const addButton = event.target.closest('[data-cart-drawer-spot-add], .o2-card__add-to-cart');
        if (!addButton || addButton.disabled) return;

        const variantId = Number(addButton.dataset.variantId || 0);
        if (!Number.isInteger(variantId) || variantId <= 0) {
          const message = this.dataset.addInvalidVariantMessage || this.dataset.addErrorMessage || '';
          this.setCardFeedback(addButton, message);
          window.O2CartOrderLimit?.showCartError(message, this);
          return;
        }

        const priceAtAdd = addButton.dataset.priceAtAdd;
        if (!o2IsValidPriceAtAdd(priceAtAdd)) {
          const message = this.dataset.addErrorMessage || '';
          this.setCardFeedback(addButton, message);
          window.O2CartOrderLimit?.showCartError(message, this);
          console.warn('[cart-drawer-product-spot-o2] Missing or invalid data-price-at-add on add button.', addButton);
          return;
        }

        this.setFeedback('');
        this.setCardFeedback(addButton, '');
        this.setButtonLoading(addButton, true);

        try {
          const requestConfig = fetchConfig('javascript');
          requestConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
          requestConfig.headers['Content-Type'] = 'application/json';
          requestConfig.body = JSON.stringify({
            items: [o2BuildCrossSellLineItem(variantId, priceAtAdd)],
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname,
          });

          const response = await fetch(routes.cart_add_url, requestConfig);
          const parsedState = await response.json();

          if (!response.ok || parsedState.status) {
            throw new Error(this.resolveCartAddErrorMessage(parsedState, this.dataset.addErrorMessage || ''));
          }

          const productCard = addButton.closest('product-card-o2');
          if (productCard) {
            await this.hideCardWithAnimation(productCard, { animate: true });
          }

          const cartDrawer = document.querySelector('cart-drawer');
          if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') {
            throw new Error('Cart drawer is not available for re-render.');
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-drawer-product-spot-o2',
            productVariantId: variantId,
            cartData: parsedState,
          }).catch(console.error);
          cartDrawer.renderContents(parsedState);
        } catch (error) {
          console.error('[cart-drawer-product-spot-o2] Add to cart failed:', error);
          const message = error?.message || this.dataset.addErrorMessage || '';
          this.setCardFeedback(addButton, message);
          window.O2CartOrderLimit?.showCartError(message, this);
        } finally {
          this.setButtonLoading(addButton, false);
          await window.O2CartOrderLimit?.syncCartState?.();
        }
      }

      resolveCartAddErrorMessage(responseJson, fallbackMessage = '') {
        const description = typeof responseJson?.description === 'string' ? responseJson.description.trim() : '';
        if (description) return description;

        const message = typeof responseJson?.message === 'string' ? responseJson.message.trim() : '';
        if (message) return message;

        const errors = responseJson?.errors;
        if (typeof errors === 'string' && errors.trim()) return errors.trim();

        if (errors && typeof errors === 'object') {
          const errorValues = Object.values(errors)
            .map((value) => {
              if (Array.isArray(value)) return value.join(' ');
              if (typeof value === 'string') return value;
              return '';
            })
            .map((value) => value.trim())
            .filter(Boolean);

          if (errorValues.length) return errorValues[0];
        }

        return fallbackMessage;
      }

      setButtonLoading(button, isLoading) {
        if (!button) return;

        button.disabled = isLoading;
        button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }

      setFeedback(message) {
        if (!this.feedbackElement) return;

        if (!message) {
          this.feedbackElement.hidden = true;
          this.feedbackElement.textContent = '';
          return;
        }

        this.feedbackElement.textContent = message;
        this.feedbackElement.hidden = false;
        this.scheduleUpdateVisibleCards();
      }

      getCardErrorElements(triggerElement) {
        if (!(triggerElement instanceof Element)) return null;

        const productCard = triggerElement.closest('product-card-o2');
        if (!productCard) return null;

        const errorWrapper = productCard.querySelector('[data-o2-card-add-error]');
        const errorText = productCard.querySelector('[data-o2-card-add-error-text]');
        if (!(errorWrapper instanceof HTMLElement) || !(errorText instanceof HTMLElement)) return null;

        return { errorWrapper, errorText };
      }

      setCardFeedback(triggerElement, message) {
        const errorElements = this.getCardErrorElements(triggerElement);
        if (!errorElements) {
          this.setFeedback(message);
          return;
        }

        if (!message) {
          errorElements.errorText.textContent = '';
          errorElements.errorWrapper.hidden = true;
          this.setFeedback('');
          return;
        }

        errorElements.errorText.textContent = message;
        errorElements.errorWrapper.hidden = false;
        this.setFeedback('');
      }

      async handleCartUpdate() {
        await this.refreshFromCartSection({ animateOnInsert: false });
      }

      async handleDrawerOpened(event) {
        const drawerRoot = this.closest('cart-drawer');
        if (!drawerRoot) return;
        if (!drawerRoot.classList.contains('active')) {
          drawerRoot.classList.remove('cart-drawer--spot-ready');
          return;
        }
        if (event?.target && event.target !== drawerRoot) return;

        await this.refreshFromCartSection({ animateOnInsert: false });
      }

      handleSpotAnimationEnd(event) {
        const entranceAnimations = new Set(['o2-cart-drawer-spot-host-reveal', 'o2-cart-drawer-spot-card-reveal']);
        if (!entranceAnimations.has(event.animationName)) return;

        const drawerRoot = this.closest('cart-drawer');
        if (!drawerRoot?.classList.contains('active')) return;

        requestAnimationFrame(() => {
          const animations = typeof this.getAnimations === 'function' ? this.getAnimations({ subtree: true }) : [];
          const runningEntranceAnimation = animations.some(
            (animation) => animation.playState !== 'finished' && entranceAnimations.has(animation.animationName)
          );

          if (!runningEntranceAnimation) {
            drawerRoot.classList.add('cart-drawer--spot-ready');
          }
        });
      }

      async refreshFromCartSection({ animateOnInsert = true } = {}) {
        if (this.isRefreshingFromCart) return;

        const spotShell = this.closest('.cart-drawer-product-spot-o2-shell');
        if (!spotShell) return;

        this.isRefreshingFromCart = true;

        try {
          const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
          if (!response.ok) return;

          const responseText = await response.text();
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const freshShell = html.querySelector('.cart-drawer-product-spot-o2-shell');

          if (!freshShell) {
            spotShell.innerHTML = '';
            return;
          }

          const drawerRoot = this.closest('cart-drawer');
          if (animateOnInsert && drawerRoot?.classList.contains('active')) {
            drawerRoot.classList.remove('cart-drawer--spot-ready');
          }

          spotShell.innerHTML = freshShell.innerHTML;
        } catch (error) {
          console.error('[cart-drawer-product-spot-o2] Refresh on cart update failed:', error);
        } finally {
          this.isRefreshingFromCart = false;
        }
      }

      scheduleUpdateVisibleCards() {
        if (this.resizeRafId) {
          cancelAnimationFrame(this.resizeRafId);
        }

        this.resizeRafId = requestAnimationFrame(() => {
          this.resizeRafId = null;
          this.updateVisibleCards();
          requestAnimationFrame(() => this.updateVisibleCards());
        });
      }

      prefersReducedMotion() {
        if (typeof window.matchMedia !== 'function') return false;
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      cssTimeToMs(value) {
        if (!value) return 0;

        const trimmedValue = value.trim();
        if (trimmedValue.endsWith('ms')) {
          const parsed = Number.parseFloat(trimmedValue);
          return Number.isFinite(parsed) ? parsed : 0;
        }

        if (trimmedValue.endsWith('s')) {
          const parsed = Number.parseFloat(trimmedValue);
          return Number.isFinite(parsed) ? parsed * 1000 : 0;
        }

        return 0;
      }

      getMotionDurationMs() {
        if (this.motionDurationMs !== null) return this.motionDurationMs;

        const localDuration = this.cssTimeToMs(getComputedStyle(this).getPropertyValue('--spot-motion-row-duration'));
        const defaultDuration = this.cssTimeToMs(
          getComputedStyle(document.documentElement).getPropertyValue('--duration-default')
        );

        this.motionDurationMs = localDuration || defaultDuration || 200;
        return this.motionDurationMs;
      }

      getMotionTimeoutMs() {
        return this.getMotionDurationMs() + 80;
      }

      clearCardAnimationTimer(card) {
        const timerId = this.cardAnimationTimers.get(card);
        if (!timerId) return;
        clearTimeout(timerId);
        this.cardAnimationTimers.delete(card);
      }

      setCardAnimationTimer(card, callback) {
        this.clearCardAnimationTimer(card);
        const timerId = window.setTimeout(() => {
          this.cardAnimationTimers.delete(card);
          callback();
        }, this.getMotionTimeoutMs());
        this.cardAnimationTimers.set(card, timerId);
      }

      async hideCardWithAnimation(card, { animate = true } = {}) {
        if (!(card instanceof HTMLElement)) return;

        const canAnimate = animate && !this.prefersReducedMotion();
        this.clearCardAnimationTimer(card);
        card.classList.remove('is-entering');

        if (!canAnimate) {
          card.classList.remove('is-leaving');
          card.hidden = true;
          card.classList.add('is-hidden');
          card.style.visibility = '';
          return;
        }

        card.hidden = false;
        card.classList.remove('is-hidden');
        card.style.visibility = '';

        await new Promise((resolve) => {
          requestAnimationFrame(() => {
            card.classList.add('is-leaving');
            this.setCardAnimationTimer(card, () => {
              card.classList.remove('is-leaving');
              card.hidden = true;
              card.classList.add('is-hidden');
              resolve();
            });
          });
        });
      }

      setCardVisibility(card, shouldHide, { animate = true, force = false } = {}) {
        if (!(card instanceof HTMLElement)) return;

        const currentlyHidden = card.hidden || card.classList.contains('is-hidden');
        if (!force && currentlyHidden === shouldHide) {
          card.style.visibility = '';
          return;
        }

        const canAnimate = animate && !this.prefersReducedMotion();
        this.clearCardAnimationTimer(card);
        card.classList.remove('is-entering', 'is-leaving');

        if (shouldHide) {
          if (!canAnimate) {
            card.hidden = true;
            card.classList.add('is-hidden');
            card.style.visibility = '';
            return;
          }

          card.hidden = false;
          card.classList.remove('is-hidden');
          card.style.visibility = '';
          requestAnimationFrame(() => {
            card.classList.add('is-leaving');
            this.setCardAnimationTimer(card, () => {
              card.classList.remove('is-leaving');
              card.hidden = true;
              card.classList.add('is-hidden');
            });
          });
          return;
        }

        card.hidden = false;
        card.classList.remove('is-hidden');
        card.style.visibility = '';
        if (!canAnimate) return;

        requestAnimationFrame(() => {
          card.classList.add('is-entering');
          this.setCardAnimationTimer(card, () => {
            card.classList.remove('is-entering');
          });
        });
      }

      cssSizeToPx(value) {
        if (!value) return 0;

        const numericValue = Number.parseFloat(value);
        if (!Number.isFinite(numericValue)) return 0;

        if (value.endsWith('rem')) {
          const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
          return numericValue * rootFontSize;
        }

        return numericValue;
      }

      updateVisibleCards() {
        if (!this.gridElement) return;

        const cards = Array.from(this.gridElement.querySelectorAll(':scope > product-card-o2'));
        if (cards.length === 0) return;

        const cardStates = cards.map((card) => ({
          card,
          wasHidden: card.hidden || card.classList.contains('is-hidden'),
        }));

        cardStates.forEach(({ card, wasHidden }) => {
          this.clearCardAnimationTimer(card);
          card.hidden = false;
          card.classList.remove('is-hidden');
          card.classList.remove('is-entering', 'is-leaving');
          card.style.visibility = wasHidden ? 'hidden' : '';
        });

        const maxCards = 4;
        const gridStyles = window.getComputedStyle(this.gridElement);
        const minCardWidthPx = this.cssSizeToPx(gridStyles.getPropertyValue('--spot-card-min-width').trim());
        const gapPx = this.cssSizeToPx(gridStyles.columnGap || gridStyles.gap || '0');
        const availableWidthPx = this.gridElement.clientWidth;

        if (availableWidthPx <= 0 || minCardWidthPx <= 0) {
          cardStates.forEach(({ card, wasHidden }) => {
            this.setCardVisibility(card, wasHidden, { animate: false, force: true });
          });
          return;
        }

        const fitCountByWidth = Math.floor((availableWidthPx + gapPx) / (minCardWidthPx + gapPx));
        const visibleCount = Math.max(1, Math.min(maxCards, fitCountByWidth));
        const drawerRoot = this.closest('cart-drawer');
        const drawerInner = drawerRoot ? drawerRoot.querySelector('.drawer__inner') : null;
        const drawerLeftEdge = drawerInner ? drawerInner.getBoundingClientRect().left : Number.POSITIVE_INFINITY;
        const cutoffRightEdge = Math.min(this.gridElement.getBoundingClientRect().right, drawerLeftEdge);

        cardStates.forEach(({ card, wasHidden }, index) => {
          let shouldHide = false;

          if (index >= maxCards) {
            shouldHide = true;
          } else if (index >= visibleCount) {
            shouldHide = true;
          } else {
            const cardRightEdge = card.getBoundingClientRect().right;
            shouldHide = cardRightEdge > cutoffRightEdge;
          }

          this.setCardVisibility(card, shouldHide, { animate: wasHidden !== shouldHide });
        });
      }
    }
  );
}
